import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import Link from '@tiptap/extension-link';
import Heading from '@tiptap/extension-heading';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon, List, Link as LinkIcon, Type as HeadingIcon } from 'lucide-react';
import Button from '@/components/ui/Button';
import { useEffect, useMemo } from 'react';
import { characterCount } from '@/lib/utils';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onImageUpload?: (file: File) => Promise<void>;
  images?: string[];
  onImageRemove?: (index: number) => void;
  maxCharacters?: number;
  /** Shown as greyed-out ghost text while the editor is empty — a worked
   * example of what to write, not a hint about the field itself (that's
   * what the label is for). */
  placeholder?: string;
}

export default function RichTextEditor({ content, onChange, maxCharacters, placeholder }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Underline,
      BulletList,
      ListItem,
      Link,
      Heading.configure({ levels: [2, 3] }),
      ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  }, []);

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  const count = useMemo(() => characterCount(content), [content]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className="border rounded-md border-gray-300 cursor-text"
      onMouseDown={() => {
        // Ensure clicking anywhere in the editor container focuses the editor
        editor.chain().focus().run();
      }}
    >
      <style>{`
        .tiptap-editor ul, .tiptap-editor ol {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        .tiptap-editor li {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          line-height: 1.1 !important;
        }
        /* Remove inner browser focus ring/border on the ProseMirror contenteditable */
        .tiptap-editor .ProseMirror,
        .tiptap-editor .ProseMirror:focus,
        .tiptap-editor .ProseMirror:focus-visible {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
        }
        .tiptap-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
      <div className="border-b border-gray-300 p-2 flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('bold') ? 'default' : 'outline'}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('italic') ? 'default' : 'outline'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('underline') ? 'default' : 'outline'}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('bulletList') ? 'default' : 'outline'}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <HeadingIcon className="h-4 w-4" /> H2
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            <HeadingIcon className="h-4 w-4" /> H3
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('link') ? 'default' : 'outline'}
            onClick={() => {
              const previousUrl = editor.getAttributes('link').href;
              const url = window.prompt('Enter the URL', previousUrl || 'https://');
              if (url === null) return;
              if (url === '') {
                editor.chain().focus().unsetLink().run();
                return;
              }
              editor.chain().focus().setLink({ href: url }).run();
            }}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </div>
        {typeof maxCharacters === 'number' && (
          <div className={`text-xs ${count > maxCharacters ? 'text-red-600' : 'text-gray-500'}`}>
            {count} / {maxCharacters}
          </div>
        )}
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[200px] focus:outline-none tiptap-editor"
      />
    </div>
  );
}